import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { Card } from './card';

interface CardActionsMenuProps {
  card: Card;
  isDefault: boolean;
  isBusy: boolean;
  onMakeDefault: (card: Card) => void;
  onDelete: (card: Card) => void;
}

/**
 * Per-card actions, behind one overflow button.
 *
 * Removing the default card is blocked rather than hidden: a director who wants
 * it gone needs to know WHY the option is inert, and a menu whose items appear
 * and disappear between cards is harder to learn than one with a steady shape.
 * The reason is stated in the menu — a tooltip would say nothing on touch.
 */
export const CardActionsMenu = ({
  card,
  isDefault,
  isBusy,
  onMakeDefault,
  onDelete,
}: CardActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={`Actions for ${card.brand} ending ${card.last4}`}
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        disabled={isBusy}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          disabled={isDefault}
          onClick={() => {
            onMakeDefault(card);
            close();
          }}
        >
          <ListItemIcon>
            <CheckCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{isDefault ? 'Already your default' : 'Make default'}</ListItemText>
        </MenuItem>

        <MenuItem
          disabled={isDefault}
          onClick={() => {
            onDelete(card);
            close();
          }}
          // `error.main` rather than the palette's red-by-name, so a theme
          // change carries through to the destructive action too.
          sx={{ color: isDefault ? undefined : 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color={isDefault ? 'disabled' : 'error'} />
          </ListItemIcon>
          <ListItemText>Remove card</ListItemText>
        </MenuItem>

        {isDefault && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            className="max-w-56 px-4 pt-1 pb-2"
          >
            Make another card your default before removing this one.
          </Typography>
        )}
      </Menu>
    </>
  );
};
